import { apiClient } from '../client';

export interface Location {
  id: string;
  name: string;
  slug: string;
  type: 'city' | 'neighborhood' | 'district';
  state: string | null;
  latitude: number;
  longitude: number;
  isActive: boolean;
  description: string | null;
  barCount?: number;
  metaDescription?: string;
  createdAt: string;
  updatedAt: string;
}

interface LocationResponse {
  success: boolean;
  location: Location;
}

interface LocationsListResponse {
  success: boolean;
  locations: Location[];
  total: number;
  limit: number;
  offset: number;
}

interface LocationStatsResponse {
  success: boolean;
  stats: {
    barCount: number;
    specialCount: number;
    eventCount: number;
  };
}

export const locationsApi = {
  /** Get location by slug (public) */
  getBySlug: async (slug: string): Promise<LocationResponse> => {
    const { data } = await apiClient.get<LocationResponse>(`/v1/locations/${slug}`);
    return data;
  },

  /** Get location stats for SEO content (public, cached) */
  getStats: async (slug: string): Promise<LocationStatsResponse> => {
    const { data } = await apiClient.get<LocationStatsResponse>(`/v1/locations/${slug}/stats`);
    return data;
  },

  /** List all locations (admin) */
  list: async (params: { activeOnly?: boolean; type?: string; limit?: number; offset?: number } = {}): Promise<LocationsListResponse> => {
    const { data } = await apiClient.get<LocationsListResponse>('/admin/locations', { params });
    return data;
  },

  /** Create a new location (admin) */
  create: async (location: Omit<Location, 'id' | 'createdAt' | 'updatedAt'>): Promise<LocationResponse> => {
    const { data } = await apiClient.post<LocationResponse>('/admin/locations', location);
    return data;
  },

  /** Update a location (admin) */
  update: async (id: string, updates: Partial<Location>): Promise<LocationResponse> => {
    const { data } = await apiClient.patch<LocationResponse>(`/admin/locations/${id}`, updates);
    return data;
  },

  /** Delete a location (admin) */
  delete: async (id: string): Promise<{ success: boolean }> => {
    const { data } = await apiClient.delete<{ success: boolean }>(`/admin/locations/${id}`);
    return data;
  },
};
